import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import { getReliableThumbnailUrl, handleThumbnailError } from "../utils/youtubeThumbnail";
import { defaultSiteSettings, SiteSettings } from "../utils/siteSettings";
import { defaultSiteCopy, SiteCopy } from "../utils/siteCopy";

const configuredApiBaseUrl = process.env.REACT_APP_API_BASE_URL;
axios.defaults.baseURL =
    configuredApiBaseUrl?.startsWith("http://") && window.location.protocol === "https:"
        ? ""
        : configuredApiBaseUrl || (window.location.protocol === "https:" ? "" : "http://localhost:5013");

interface Content {
    id: number;
    title: string;
    description: string;
    content_type: "hero_video" | "works" | "product" | "pr";
    category_name?: string | null;
    youtube_url: string;
    youtube_id: string;
    thumbnail_url: string;
    custom_thumbnail: string;
    custom_description: string;
    custom_published_at?: string | null;
    published_at?: string | null;
    view_count: number;
    like_count: number;
    created_at: string;
}

const Page = styled.main`
    background: #f6f7f4;
    color: #121820;
    min-height: 70vh;
`;

const Hero = styled.section`
    background: #11161c;
    color: #f8faf7;
`;

const Inner = styled.div`
    width: min(1180px, calc(100% - 40px));
    margin: 0 auto;
    padding: 64px 0;

    @media (max-width: 720px) {
        width: min(100% - 28px, 1180px);
        padding: 42px 0;
    }
`;

const HeroGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 0.85fr) minmax(320px, 1.15fr);
    gap: 44px;
    align-items: center;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const Eyebrow = styled.p`
    margin: 0 0 14px;
    color: #c69b62;
    font-size: 0.78rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

const Title = styled.h1`
    margin: 0;
    font-size: clamp(2.2rem, 5vw, 4.8rem);
    line-height: 1.02;
    font-weight: 850;
`;

const Copy = styled.p`
    margin: 18px 0 0;
    color: inherit;
    opacity: 0.78;
    line-height: 1.75;
    font-size: 1rem;
`;

const Thumb = styled.img`
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    display: block;
    border-radius: 8px;
    background: #000;
`;

const MetaGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin-top: 28px;

    @media (max-width: 760px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const MetaBox = styled.div`
    border-top: 1px solid rgba(248, 250, 247, 0.24);
    padding-top: 12px;
`;

const MetaLabel = styled.p`
    margin: 0 0 6px;
    color: #aab2b8;
    font-size: 0.78rem;
`;

const MetaValue = styled.p`
    margin: 0;
    font-weight: 800;
`;

const BodyGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 0.8fr) minmax(320px, 1.2fr);
    gap: 44px;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const SectionTitle = styled.h2`
    margin: 0;
    font-size: clamp(1.8rem, 3vw, 3rem);
    line-height: 1.08;
`;

const Panel = styled.div`
    display: grid;
    gap: 18px;
`;

const Card = styled.div`
    border: 1px solid #d7ddd5;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.62);
    padding: 20px;
`;

const CardTitle = styled.h3`
    margin: 0 0 10px;
    font-size: 1rem;
`;

const List = styled.ul`
    margin: 0;
    padding-left: 18px;
    color: #4b5967;
    line-height: 1.8;
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 26px;
`;

const ButtonLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 0 16px;
    border-radius: 8px;
    background: #11161c;
    color: #f8faf7;
    text-decoration: none;
    font-weight: 800;
`;

const ExternalLink = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 0 16px;
    border-radius: 8px;
    border: 1px solid #11161c;
    color: #11161c;
    text-decoration: none;
    font-weight: 800;
`;

const Empty = styled.div`
    width: min(720px, calc(100% - 32px));
    margin: 0 auto;
    padding: 80px 0;
    text-align: center;
`;

const formatDate = (value?: string | null) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value.split("T")[0];
    return date.toLocaleDateString("ko-KR");
};

const formatNumber = (value?: number) => Number(value || 0).toLocaleString("ko-KR");

const ProjectDetail: React.FC = () => {
    const { id } = useParams();
    const [content, setContent] = useState<Content | null>(null);
    const [settings, setSettings] = useState<SiteSettings>(defaultSiteSettings);
    const [copy, setCopy] = useState<SiteCopy>(defaultSiteCopy);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const [contentRes, settingsRes, copyRes] = await Promise.all([
                    axios.get(`/api/content/${id}`),
                    axios.get("/api/site-settings").catch(() => ({ data: { data: defaultSiteSettings } })),
                    axios.get("/api/site-copy").catch(() => ({ data: { data: defaultSiteCopy } })),
                ]);
                setContent(contentRes.data.data || null);
                setSettings({ ...defaultSiteSettings, ...(settingsRes.data.data || {}) });
                setCopy({ ...defaultSiteCopy, ...(copyRes.data.data || {}) });
            } catch (error) {
                console.error("Error fetching project detail:", error);
                setContent(null);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [id]);

    const scope = useMemo(() => {
        if (!content) return [];
        const base = ["기획 방향 정리", "영상 편집", "색감 및 사운드 마감"];
        if (content.content_type === "product") return ["제품 연출", "디테일 컷 구성", ...base.slice(1)];
        if (content.content_type === "pr") return ["브랜드 메시지 구조화", "캠페인 톤 설계", ...base.slice(1)];
        return base;
    }, [content]);

    if (loading) {
        return <Empty>Loading...</Empty>;
    }

    if (!content) {
        return (
            <Empty>
                <SectionTitle>프로젝트를 찾을 수 없습니다.</SectionTitle>
                <Actions style={{ justifyContent: "center" }}>
                    <ButtonLink to="/works">목록으로 돌아가기</ButtonLink>
                </Actions>
            </Empty>
        );
    }

    if (!settings.show_project_detail) {
        return (
            <Empty>
                <SectionTitle>{copy.project_detail_hidden_title}</SectionTitle>
                <Copy>{copy.project_detail_hidden_body}</Copy>
                <Actions style={{ justifyContent: "center" }}>
                    <ButtonLink to="/works">목록으로 돌아가기</ButtonLink>
                </Actions>
            </Empty>
        );
    }

    const description = content.custom_description || content.description || "프로젝트의 목적과 톤에 맞춰 완성한 영상 작업입니다.";

    return (
        <Page>
            <Hero>
                <Inner>
                    <HeroGrid>
                        <div>
                            <Eyebrow>{content.category_name || content.content_type}</Eyebrow>
                            <Title>{content.title}</Title>
                            <Copy>{description}</Copy>
                            <MetaGrid>
                                <MetaBox>
                                    <MetaLabel>Type</MetaLabel>
                                    <MetaValue>{content.content_type.toUpperCase()}</MetaValue>
                                </MetaBox>
                                <MetaBox>
                                    <MetaLabel>Published</MetaLabel>
                                    <MetaValue>{formatDate(content.custom_published_at || content.published_at || content.created_at)}</MetaValue>
                                </MetaBox>
                                <MetaBox>
                                    <MetaLabel>Views</MetaLabel>
                                    <MetaValue>{formatNumber(content.view_count)}</MetaValue>
                                </MetaBox>
                                <MetaBox>
                                    <MetaLabel>Channel</MetaLabel>
                                    <MetaValue>YouTube</MetaValue>
                                </MetaBox>
                            </MetaGrid>
                        </div>
                        <Thumb src={getReliableThumbnailUrl(content.custom_thumbnail || content.thumbnail_url)} alt={content.title} onError={(event) => handleThumbnailError(event.currentTarget)} />
                    </HeroGrid>
                </Inner>
            </Hero>

            <Inner>
                <BodyGrid>
                    <div>
                        <Eyebrow>Case Study</Eyebrow>
                        <SectionTitle>{copy.project_detail_case_heading}</SectionTitle>
                        <Copy>{copy.project_detail_case_body}</Copy>
                        <Actions>
                            {content.youtube_url && <ExternalLink href={content.youtube_url} target="_blank" rel="noopener noreferrer">YouTube 보기</ExternalLink>}
                            <ButtonLink to="/contacts">프로젝트 문의</ButtonLink>
                        </Actions>
                    </div>
                    <Panel>
                        <Card>
                            <CardTitle>제작 목적</CardTitle>
                            <Copy>{description}</Copy>
                        </Card>
                        <Card>
                            <CardTitle>담당 범위</CardTitle>
                            <List>
                                {scope.map((item) => <li key={item}>{item}</li>)}
                            </List>
                        </Card>
                        <Card>
                            <CardTitle>납품 포인트</CardTitle>
                            <List>
                                <li>브랜드 톤에 맞춘 화면 밀도와 템포</li>
                                <li>웹, SNS, 프레젠테이션에서 활용 가능한 결과물</li>
                                <li>수정과 납품 과정을 고려한 안정적인 제작 흐름</li>
                            </List>
                        </Card>
                    </Panel>
                </BodyGrid>
            </Inner>
        </Page>
    );
};

export default ProjectDetail;
